// The company fiscal-year setting (item 81). yearStartMonth: 1 = January … 12 =
// December (the day is always the 1st). Default 1 = calendar year.
export interface CompanyFiscal {
  yearStartMonth: number;
}

const pad = (n: number): string => String(n).padStart(2, '0');
const ymd = (d: Date): string => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;

// Inclusive [from, to] yyyy-MM-dd window for the CURRENT fiscal year given the
// company's start month (1..12). Shared by the dashboard aggregates (item 68) and
// the grid "This year" date preset (item 71) so the two always agree. With the
// default start month = January this is just the calendar year.
export const fiscalYearRange = (
  yearStartMonth: number | undefined,
  now: Date = new Date(),
): { from: string; to: string } => {
  const m = Math.min(12, Math.max(1, Math.round(yearStartMonth || 1))); // 1..12
  const curMonth = now.getMonth() + 1; // 1..12
  // if we're before the start month, the fiscal year began LAST calendar year
  const startYear = curMonth >= m ? now.getFullYear() : now.getFullYear() - 1;
  const from = `${startYear}-${pad(m)}-01`;
  // inclusive last day = the day before next fiscal year's start month
  const to = ymd(new Date(startYear + 1, m - 1, 0));
  return { from, to };
};

// does a date-only / ISO date string fall within an inclusive [from, to] window?
export const inRange = (
  raw: string | undefined,
  range: { from: string; to: string },
): boolean => {
  if (!raw) return false;
  const day = String(raw).slice(0, 10); // yyyy-MM-dd (handles "...T00:00:00")
  return day >= range.from && day <= range.to;
};

// item 285 — the FISCAL YEAR a date falls in, labelled by the calendar year the fiscal
// year STARTED (e.g. start month July: Aug 2025 and Mar 2026 are both FY 2025). Used by
// the Sales Bible year switcher. Returns null for an empty/invalid date.
export const fiscalYearOf = (
  raw: string | undefined,
  yearStartMonth: number | undefined,
): number | null => {
  if (!raw) return null;
  const day = String(raw).slice(0, 10);
  const year = Number(day.slice(0, 4));
  const month = Number(day.slice(5, 7)); // 1..12
  if (!year || !month) return null;
  const m = Math.min(12, Math.max(1, Math.round(yearStartMonth || 1)));
  return month >= m ? year : year - 1;
};

// the current fiscal year's start-year label
export const currentFiscalYear = (
  yearStartMonth: number | undefined,
  now: Date = new Date(),
): number => fiscalYearOf(
  `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`,
  yearStartMonth,
) as number;
